function getItemKey(item) {
    return item?.documentId ?? item?.id ?? null;
}

function upsertCollectionItem(items, item) {
    if (!item) return Array.isArray(items) ? items : [];

    const currentItems = Array.isArray(items) ? items : [];
    const key = getItemKey(item);
    const index = currentItems.findIndex((current) => getItemKey(current) === key);

    if (index >= 0) {
        const next = [...currentItems];
        next[index] = {...next[index], ...item};
        return next;
    }

    return [...currentItems, item];
}

function removeCollectionItem(items, item) {
    const currentItems = Array.isArray(items) ? items : [];
    const key = getItemKey(item);
    return currentItems.filter((current) => getItemKey(current) !== key);
}

function mergeCollectionItems(items, incomingItems) {
    if (!Array.isArray(incomingItems) || !incomingItems.length) {
        return Array.isArray(items) ? items : [];
    }

    const currentItems = Array.isArray(items) ? items : [];
    const next = [...currentItems];

    incomingItems.forEach((item) => {
        if (!item) return;
        const key = getItemKey(item);
        const index = next.findIndex((current) => getItemKey(current) === key);

        if (index >= 0) {
            next[index] = {...next[index], ...item};
            return;
        }

        next.push(item);
    });

    return next;
}

function getRealtimePayload(event) {
    return event?.data?.data ?? event?.data ?? event?.payload?.data ?? event?.payload ?? event ?? null;
}

function getRealtimeType(event) {
    return event?.type ?? event?.event ?? event?.action ?? "";
}

function normalizeEntityKind(value) {
    if (!value) return null;

    const normalized = String(value)
        .replace(/^api::/i, "")
        .replace(/[./]/g, ":")
        .toLowerCase();

    if (normalized.includes("workspace-membership") || normalized.includes("workspacemembership")) {
        return "workspaceMembership";
    }

    if (normalized.includes("user-role") || normalized.includes("userrole")) {
        return "userRole";
    }

    if (normalized.includes("workspace")) {
        return "workspace";
    }

    if (normalized.includes("chat-participant") || normalized.includes("chatparticipant")) {
        return "chatParticipant";
    }

    if (normalized.includes("chat-message") || normalized.includes("chatmessage")) {
        return "chatMessage";
    }

    if (normalized.includes("user-event-log") || normalized.includes("usereventlog") || normalized.includes("user-notification")) {
        return "userEventLog";
    }

    if (normalized.includes("chat")) {
        return "chat";
    }

    if (normalized.includes("task")) {
        return "task";
    }

    if (normalized.includes("milestone")) {
        return "deliverable";
    }

    if (normalized.includes("deliverable")) {
        return "deliverable";
    }

    if (normalized.includes("project")) {
        return "project";
    }

    if (normalized.includes("goal")) {
        return "goal";
    }

    if (normalized.includes("team")) {
        return "team";
    }

    if (normalized.includes("customer")) {
        return "customer";
    }

    return null;
}

function inferEntityKindFromPayload(payload) {
    if (!payload || typeof payload !== "object") return null;

    if (payload.member && Array.isArray(payload.user_roles)) {
        return "workspaceMembership";
    }

    if (payload.role && payload.scope_type && payload.workspace_membership) {
        return "userRole";
    }

    if (payload.chat && payload.user && (payload.messageContent !== undefined || payload.messageType)) {
        return "chatMessage";
    }

    if (payload.chat && payload.user && (payload.unreadCount !== undefined || payload.participantState)) {
        return "chatParticipant";
    }

    if (payload.owner && (payload.eventSurface || payload.displayType || payload.eventCategory)) {
        return "userEventLog";
    }

    if (Array.isArray(payload.users) && (payload.type === "private" || payload.type === "group")) {
        return "chat";
    }

    if (payload.workspace && Array.isArray(payload.goals) && Array.isArray(payload.projects) && Array.isArray(payload.teams)) {
        return "workspace";
    }

    if (payload.taskStatus || payload.parentTask || payload.childrenTasks || payload.task_type) {
        return "task";
    }

    if (payload.deliverableStatus || payload.milestoneStatus || payload.impactWeight || payload.milestoneType) {
        return "deliverable";
    }

    if (payload.goalStatus || payload.parentGoal || payload.cycle || payload.mapX !== undefined || payload.mapY !== undefined) {
        return "goal";
    }

    if (payload.projectStatus || payload.deliverables || payload.members || payload.portfolios || payload.rolesConfig) {
        return "project";
    }

    if (payload.teamRules || payload.accessConfigs || payload.teamHealthIndex || payload.iconColor) {
        return "team";
    }

    if (payload.customerStatus || payload.customerType || payload.phonenumber || payload.moreDetails) {
        return "customer";
    }

    return null;
}

function getRealtimeEntityKind(event) {
    const payload = getRealtimePayload(event);
    const candidates = [
        event?.kind,
        event?.entity,
        event?.entityType,
        event?.contentType,
        event?.type,
        event?.event,
        event?.action,
        payload?.__type,
        payload?.type,
    ];

    for (const candidate of candidates) {
        const kind = normalizeEntityKind(candidate);
        if (kind) return kind;
    }

    const typeValue = getRealtimeType(event);
    const normalizedType = normalizeEntityKind(typeValue);
    if (normalizedType) return normalizedType;

    return inferEntityKindFromPayload(payload);
}

function getRealtimeDocumentId(event) {
    const payload = getRealtimePayload(event);
    const candidates = [
        event?.documentId,
        event?.id,
        event?.data?.documentId,
        event?.data?.id,
        event?.payload?.documentId,
        event?.payload?.id,
        payload?.documentId,
        payload?.id,
        payload?.workspace?.documentId,
        payload?.workspace?.id,
    ];

    return candidates.find((value) => value !== undefined && value !== null && value !== "");
}

function getRealtimeAction(event) {
    const candidates = [event?.action, event?.type, event?.event, event?.kind];
    const rawValue = candidates.find(Boolean);
    if (!rawValue) return "upsert";

    const normalized = String(rawValue).toLowerCase();

    if (normalized.includes("delete") || normalized.includes("remove")) {
        return "remove";
    }

    if (normalized.includes("archive")) {
        return "archive";
    }

    return "upsert";
}

function getRealtimeEventDescriptor(event) {
    const payload = getRealtimePayload(event);
    const kind = getRealtimeEntityKind(event);
    const documentId = getRealtimeDocumentId(event);
    const action = getRealtimeAction(event);
    const signatureParts = [
        kind || "unknown",
        documentId || "",
        action,
        payload?.updatedAt || payload?.createdAt || payload?.publishedAt || "",
    ];

    return {
        event,
        payload,
        kind,
        documentId,
        action,
        signature: signatureParts.join(":"),
    };
}

function isRemovedRealtimeEvent(event, entity) {
    const action = getRealtimeAction(event);
    return action === "remove" || action === "archive" || Boolean(entity?.archivedAt);
}

function patchProjectTasks(project, task, remove = false) {
    const currentTasks = Array.isArray(project?.tasks) ? project.tasks : [];
    const taskKey = getItemKey(task);
    const hasTask = currentTasks.some((item) => getItemKey(item) === taskKey);
    const relatedProjectIds = Array.isArray(task?.projects) ? task.projects.map(getItemKey).filter(Boolean) : [];
    const projectKey = getItemKey(project);
    const isRelatedNow = relatedProjectIds.includes(projectKey);

    if (!hasTask && !isRelatedNow) {
        return project;
    }

    if (remove || (!isRelatedNow && hasTask)) {
        const nextTasks = removeCollectionItem(currentTasks, task);
        return nextTasks === currentTasks ? project : {...project, tasks: nextTasks};
    }

    const nextTasks = upsertCollectionItem(currentTasks, task);
    return nextTasks === currentTasks ? project : {...project, tasks: nextTasks};
}

function patchProjectDeliverables(project, deliverable, remove = false) {
    const currentDeliverables = Array.isArray(project?.deliverables) ? project.deliverables : [];
    const deliverableKey = getItemKey(deliverable);
    const hasDeliverable = currentDeliverables.some((item) => getItemKey(item) === deliverableKey);
    const relatedProjectIds = [];

    if (deliverable?.project) {
        relatedProjectIds.push(getItemKey(deliverable.project));
    }

    if (Array.isArray(deliverable?.projects)) {
        relatedProjectIds.push(...deliverable.projects.map(getItemKey).filter(Boolean));
    }

    const projectKey = getItemKey(project);
    const isRelatedNow = relatedProjectIds.includes(projectKey);

    if (!hasDeliverable && !isRelatedNow) {
        return project;
    }

    if (remove || (!isRelatedNow && hasDeliverable)) {
        const nextDeliverables = removeCollectionItem(currentDeliverables, deliverable);
        return nextDeliverables === currentDeliverables ? project : {...project, deliverables: nextDeliverables};
    }

    const nextDeliverables = upsertCollectionItem(currentDeliverables, deliverable);
    return nextDeliverables === currentDeliverables ? project : {...project, deliverables: nextDeliverables};
}

function patchTaskCollections(workspace, task, remove = false) {
    if (!workspace) return workspace;

    const nextWorkspace = {...workspace};
    nextWorkspace.tasks = remove
        ? removeCollectionItem(nextWorkspace.tasks, task)
        : upsertCollectionItem(nextWorkspace.tasks, task);

    if (Array.isArray(workspace.projects)) {
        nextWorkspace.projects = workspace.projects.map((project) => patchProjectTasks(project, task, remove));
    }

    return nextWorkspace;
}

function patchCollection(workspace, fieldName, entity, remove = false) {
    const currentItems = Array.isArray(workspace?.[fieldName]) ? workspace[fieldName] : [];
    const nextItems = remove
        ? removeCollectionItem(currentItems, entity)
        : upsertCollectionItem(currentItems, entity);

    if (nextItems === currentItems) {
        return workspace;
    }

    return {
        ...workspace,
        [fieldName]: nextItems,
    };
}

function patchWorkspaceMembershipData(workspaceMembershipData, kind, entity, remove = false) {
    if (!workspaceMembershipData?.workspace || !kind) {
        return workspaceMembershipData;
    }

    const workspace = workspaceMembershipData.workspace;

    if (kind === "userRole") {
        const nextUserRoles = remove
            ? removeCollectionItem(workspaceMembershipData.user_roles, entity)
            : upsertCollectionItem(workspaceMembershipData.user_roles, entity);
        const activeMembershipKey = getItemKey(workspaceMembershipData);
        const activeMemberKey = getItemKey(workspaceMembershipData.member);
        const workspaceMemberships = Array.isArray(workspace.workspace_memberships)
            ? workspace.workspace_memberships.map((membership) => {
                const isActiveMembership = getItemKey(membership) === activeMembershipKey
                    || getItemKey(membership?.member) === activeMemberKey;
                return isActiveMembership ? {...membership, user_roles: nextUserRoles} : membership;
            })
            : workspace.workspace_memberships;

        return {
            ...workspaceMembershipData,
            user_roles: nextUserRoles,
            workspace: {
                ...workspace,
                workspace_memberships: workspaceMemberships,
            },
        };
    }

    if (kind === "task") {
        return {
            ...workspaceMembershipData,
            workspace: patchTaskCollections(workspace, entity, remove),
        };
    }

    if (kind === "deliverable") {
        const nextWorkspace = {
            ...workspace,
        };

        if (Array.isArray(workspace.projects)) {
            nextWorkspace.projects = workspace.projects.map((project) => patchProjectDeliverables(project, entity, remove));
        }

        return {
            ...workspaceMembershipData,
            workspace: nextWorkspace,
        };
    }

    if (kind === "project") {
        const nextWorkspace = patchCollection(workspace, "projects", entity, remove);
        if (!remove && Array.isArray(entity?.tasks)) {
            nextWorkspace.tasks = mergeCollectionItems(nextWorkspace.tasks, entity.tasks);
        }

        return {
            ...workspaceMembershipData,
            workspace: nextWorkspace,
        };
    }

    if (kind === "goal") {
        return {
            ...workspaceMembershipData,
            workspace: patchCollection(workspace, "goals", entity, remove),
        };
    }

    if (kind === "team") {
        return {
            ...workspaceMembershipData,
            workspace: patchCollection(workspace, "teams", entity, remove),
        };
    }

    if (kind === "customer") {
        return {
            ...workspaceMembershipData,
            workspace: patchCollection(workspace, "customers", entity, remove),
        };
    }

    if (kind === "workspaceMembership") {
        return {
            ...workspaceMembershipData,
            workspace: {
                ...workspace,
                workspace_memberships: remove
                    ? removeCollectionItem(workspace.workspace_memberships, entity)
                    : upsertCollectionItem(workspace.workspace_memberships, entity),
            },
        };
    }

    return workspaceMembershipData;
}

export {
    getItemKey,
    getRealtimeAction,
    getRealtimeDocumentId,
    getRealtimeEntityKind,
    getRealtimeEventDescriptor,
    getRealtimePayload,
    getRealtimeType,
    isRemovedRealtimeEvent,
    mergeCollectionItems,
    patchWorkspaceMembershipData,
    removeCollectionItem,
    upsertCollectionItem,
};
